<script setup lang="ts">
import {
  type EditorFileBrowserFilter,
  type EditorFileBrowserSelection,
  type EditorFileBrowserScope,
} from "#bb/lib/editorFileBrowser";
import {
  applyEditorFileBrowserSelection,
  browserScopesForEditorPathInput,
  deriveEditorReportCachePaths,
  editorPathInputCandidateContextMessage,
  inferEditorPathInputScope,
  normalizeEditorPathInputScopes,
  resolveEditorPathInputPreview,
  switchEditorPathInputScope,
  type EditorPathInputScope,
} from "#bb/lib/editorPathInput";
import {
  classifyAuthoringPath,
  type AuthoringPathFieldMode,
} from "#bb/lib/pathContract";

interface Props {
  id?: string;
  mode?: AuthoringPathFieldMode;
  scopes?: readonly EditorPathInputScope[];
  defaultScope?: EditorPathInputScope;
  allowHttpUrls?: boolean;
  userId?: string | null;
  contentTypeFilter?: EditorFileBrowserFilter;
  includeContentTypes?: readonly string[];
  excludeContentTypes?: readonly string[];
  placeholder?: string;
  hint?: string | null;
  ariaLabel?: string;
  browseTitle?: string | null;
  browseDescription?: string | null;
  disabled?: boolean;
  readonly?: boolean;
  resourceDefinitionPath?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  mode: "existing-file",
  scopes: undefined,
  defaultScope: "site",
  allowHttpUrls: false,
  userId: null,
  contentTypeFilter: "all",
  includeContentTypes: () => [],
  excludeContentTypes: () => [],
  placeholder: "",
  hint: null,
  ariaLabel: "Path",
  browseTitle: null,
  browseDescription: null,
  disabled: false,
  readonly: false,
  resourceDefinitionPath: null,
});

const emit = defineEmits<{
  browse: [scope: EditorFileBrowserScope];
  select: [value: string, selection: EditorFileBrowserSelection];
  scopeChange: [scope: EditorPathInputScope, value: string];
}>();

const model = defineModel<string>({ default: "" });
const browserOpen = shallowRef(false);
const scopeSwitchMessage = shallowRef<string | null>(null);
const renderFeatures = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});
const effectiveResourceDefinitionPath = computed(
  () => props.resourceDefinitionPath
    || renderFeatures.resourceDefinitionPath
    || null,
);

const allowedScopes = computed(() =>
  normalizeEditorPathInputScopes(props.scopes, {
    mode: props.mode,
    allowHttpUrls: props.allowHttpUrls,
  }),
);
const browserScopes = computed(() =>
  browserScopesForEditorPathInput(allowedScopes.value),
);
const selectedScope = shallowRef<EditorPathInputScope>(
  inferEditorPathInputScope(
    model.value,
    allowedScopes.value,
    props.defaultScope,
  ),
);

const classification = computed(() =>
  classifyAuthoringPath(model.value, {
    allowHttpUrls: props.allowHttpUrls,
    resourceDefinitionPath: effectiveResourceDefinitionPath.value,
  }),
);
const warning = computed(() => classification.value.warning);
const resolvedPreview = computed(() =>
  resolveEditorPathInputPreview(model.value, props.userId),
);
const candidateContextMessage = computed(() =>
  editorPathInputCandidateContextMessage(
    selectedScope.value,
    props.mode,
    props.userId,
  ),
);
const reportCachePaths = computed(() =>
  props.mode === "report-cache"
    ? deriveEditorReportCachePaths(model.value)
    : null,
);
const canBrowse = computed(() =>
  !props.disabled
  && !props.readonly
  && selectedScope.value !== "external"
  && browserScopes.value.length > 0,
);
const browserDefaultScope = computed<EditorFileBrowserScope>(() => {
  if (
    selectedScope.value !== "external"
    && browserScopes.value.includes(selectedScope.value)
  ) {
    return selectedScope.value;
  }
  return browserScopes.value[0] ?? "site";
});
const browseLabel = computed(() => {
  if (selectedScope.value === "external") {
    return "External URLs are entered manually";
  }
  return props.mode === "existing-file"
    ? "Browse files"
    : "Browse folders";
});

function scopeLabel(scope: EditorPathInputScope): string {
  if (scope === "user") return "Candidate";
  if (scope === "system") return "System";
  if (scope === "external") return "URL";
  return "Site";
}

function updateManualValue(value: string | number) {
  model.value = String(value);
  scopeSwitchMessage.value = null;

  const inferred = inferEditorPathInputScope(
    model.value,
    allowedScopes.value,
    selectedScope.value,
  );
  if (
    classification.value.kind === "url"
    || classification.value.scope === "user"
    || classification.value.scope === "system"
  ) {
    selectedScope.value = inferred;
  }
}

function changeScope(value: unknown) {
  if (
    typeof value !== "string"
    || !allowedScopes.value.includes(value as EditorPathInputScope)
  ) {
    return;
  }

  const targetScope = value as EditorPathInputScope;
  const result = switchEditorPathInputScope(model.value, targetScope);
  if (result.reason) {
    scopeSwitchMessage.value = result.reason === "unknown-user-path"
      ? "This concrete user path was not rewritten. Replace its owner with <userId> before changing scope."
      : "This path contains . or .. segments, so its scope was not changed.";
    return;
  }

  selectedScope.value = targetScope;
  scopeSwitchMessage.value = null;
  if (result.changed) model.value = result.value;
  emit("scopeChange", targetScope, model.value);
}

function openBrowser() {
  if (!canBrowse.value) return;
  emit("browse", browserDefaultScope.value);
  browserOpen.value = true;
}

function applySelection(
  selectedPath: string,
  selection: EditorFileBrowserSelection,
) {
  const value = applyEditorFileBrowserSelection(
    model.value,
    selectedPath,
    props.mode,
  );
  model.value = value;
  selectedScope.value = selection.scope;
  scopeSwitchMessage.value = null;
  emit("select", value, selection);
}

watch(
  allowedScopes,
  (scopes) => {
    if (!scopes.includes(selectedScope.value)) {
      selectedScope.value = inferEditorPathInputScope(
        model.value,
        scopes,
        props.defaultScope,
      );
    }
  },
  { immediate: true },
);

watch(
  () => model.value,
  (value) => {
    const pathScope = classifyAuthoringPath(value, {
      allowHttpUrls: true,
    }).scope;
    if (
      pathScope !== "unknown"
      && allowedScopes.value.includes(pathScope)
    ) {
      selectedScope.value = pathScope;
    }
  },
);
</script>

<template>
  <div class="space-y-1.5" data-slot="editor-path-input">
    <div class="flex min-w-0 gap-1.5">
      <Select
        v-if="allowedScopes.length > 1"
        :model-value="selectedScope"
        :disabled="disabled || readonly"
        @update:model-value="changeScope"
      >
        <SelectTrigger
          class="h-8 w-[104px] shrink-0 rounded-sm bg-muted px-2 text-[12px]"
          aria-label="Path scope"
          data-testid="path-scope"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="scope in allowedScopes"
            :key="scope"
            :value="scope"
          >
            {{ scopeLabel(scope) }}
          </SelectItem>
        </SelectContent>
      </Select>

      <Input
        :id="id"
        :model-value="model"
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        :disabled="disabled"
        :readonly="readonly"
        class="h-8 min-w-0 flex-1 rounded-sm bg-muted font-mono text-[13px]"
        data-testid="path-input"
        autocomplete="off"
        spellcheck="false"
        @update:model-value="updateManualValue"
      />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        class="size-8 shrink-0 rounded-sm"
        :disabled="!canBrowse"
        :aria-label="browseLabel"
        :title="browseLabel"
        data-testid="path-browse"
        @click="openBrowser"
      >
        <Icon name="lucide:ellipsis" class="size-4" />
      </Button>
    </div>

    <p v-if="hint" class="text-[11.5px] text-muted-foreground">
      {{ hint }}
    </p>

    <p
      v-if="warning"
      class="flex items-start gap-1.5 text-[11.5px] text-warning"
      data-testid="path-warning"
    >
      <Icon name="lucide:triangle-alert" class="mt-0.5 size-3.5 shrink-0" />
      <span>{{ warning.message }}</span>
    </p>

    <p
      v-if="scopeSwitchMessage"
      class="flex items-start gap-1.5 text-[11.5px] text-warning"
      data-testid="path-scope-warning"
    >
      <Icon name="lucide:triangle-alert" class="mt-0.5 size-3.5 shrink-0" />
      <span>{{ scopeSwitchMessage }}</span>
    </p>

    <p
      v-if="candidateContextMessage"
      class="flex items-start gap-1.5 text-[11.5px] text-muted-foreground"
      data-testid="path-candidate-context"
    >
      <Icon name="lucide:info" class="mt-0.5 size-3.5 shrink-0" />
      <span>{{ candidateContextMessage }}</span>
    </p>

    <p
      v-if="resolvedPreview"
      class="truncate font-mono text-[10.5px] text-muted-foreground"
      data-testid="path-resolved-preview"
    >
      Resolved: {{ resolvedPreview }}
    </p>

    <div
      v-if="reportCachePaths"
      class="space-y-0.5 rounded-sm border bg-muted/35 px-2.5 py-2 font-mono text-[10.5px] text-muted-foreground"
      data-testid="report-cache-paths"
    >
      <p class="truncate">
        <span class="font-sans font-medium text-foreground">Candidate</span>
        {{ reportCachePaths.user }}
      </p>
      <p class="truncate">
        <span class="font-sans font-medium text-foreground">System</span>
        {{ reportCachePaths.system }}
      </p>
    </div>

    <EditorFileBrowserDialog
      v-model:open="browserOpen"
      :path="model"
      :mode="mode"
      :scopes="browserScopes"
      :default-scope="browserDefaultScope"
      :preview-candidate-id="userId"
      :content-type-filter="contentTypeFilter"
      :include-content-types="includeContentTypes"
      :exclude-content-types="excludeContentTypes"
      :title="browseTitle"
      :description="browseDescription"
      :resource-definition-path="effectiveResourceDefinitionPath"
      @select="applySelection"
    />
  </div>
</template>
